'use client';

import { useModals } from '@/contexts/ModalContext';
import {
  TelegramProductExtendedDto,
  useTelegramConnectionsUserUpsert,
  useTelegramProductsUserGetContacts,
} from '@/generated';
import { useTranslations } from 'next-intl';
import { IconBrandTelegram, IconBrandWhatsapp } from '@tabler/icons-react';
import clsx from 'clsx';
import { useMediaQuery } from 'usehooks-ts';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { useCallback, useState } from 'react';
import { sendGTMEvent } from '@next/third-parties/google';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { useUserData } from '@/contexts/LoginContext';

type Props = {
  onOk: () => void;
  product: TelegramProductExtendedDto;
};

export const WriteMessengerModal: React.FC<Props> = ({ onOk, product }) => {
  const modals = useModals();
  const t = useTranslations('modals');

  const userData = useUserData();

  const { data: contacts, isLoading: isContactsLoading } = useTelegramProductsUserGetContacts(
    product.link,
    { contactAction: 'chat' },
    { query: { staleTime: Infinity }, client: { handle401: false } },
  );
  const [isUploading, setIsUploading] = useState(false);

  const { mutateAsync: createConnection } = useTelegramConnectionsUserUpsert();

  const onClickWhatsApp = useCallback(async () => {
    sendGTMEvent({
      event: 'press_chat_button_whatsapp',
    });
    if (!userData) return;
    setIsUploading(true);
    await createConnection({
      data: {
        socialNetwork: 'WHATSAPP',
        telegramProductId: product.id,
      },
    });
    setIsUploading(false);
  }, [createConnection, product, userData]);

  const onClickTelegram = useCallback(async () => {
    sendGTMEvent({
      event: 'press_chat_button_telegram',
    });
    if (!userData) return;
    setIsUploading(true);
    await createConnection({
      data: {
        socialNetwork: 'TELEGRAM',
        telegramProductId: product.id,
      },
    });
    setIsUploading(false);
  }, [createConnection, product, userData]);

  const onClose = () => {
    onOk();
    modals.close(`write-messenger-modal`);
  };

  const isLoading = isContactsLoading || isUploading;

  const messengers = [
    {
      key: 'whatsapp',
      label: t('writeMessenger.whatsapp'),
      Icon: IconBrandWhatsapp,
      href: contacts?.whatsappUrl,
      onClick: onClickWhatsApp,
      className: 'bg-[#1DAA61] hover:bg-[#189553]',
    },
    {
      key: 'telegram',
      label: t('writeMessenger.telegram'),
      Icon: IconBrandTelegram,
      href: contacts?.telegramUrl,
      onClick: onClickTelegram,
      className: 'bg-[#229ED9] hover:bg-[#1E8DC2]',
    },
  ];

  const isMobile = useMediaQuery('(max-width: 767px)');

  const content = (
    <>
      {isLoading ? (
        <div className='flex flex-col gap-[8px] w-full'>
          <Skeleton className='h-[48px] rounded-[12px]' />
          <Skeleton className='h-[48px] rounded-[12px]' />
        </div>
      ) : (
        <div className='flex flex-col gap-[8px] w-full'>
          {messengers.map(({ key, label, Icon, href, onClick, className }) => (
            <Button key={key} asChild size='full' className={clsx('relative px-[52px]', className)}>
              <a onClick={onClick} href={href || ''} target='_blank' rel='noopener noreferrer'>
                <Icon stroke={1.75} className='absolute start-[16px] size-6' />
                <span className='truncate'>{label}</span>
              </a>
            </Button>
          ))}
        </div>
      )}
    </>
  );

  if (isMobile) {
    return (
      <Drawer open onOpenChange={(open) => !open && onClose()}>
        <DrawerContent className='rounded-t-[24px] bg-bg-day'>
          <DrawerHeader className='items-center px-[16px] pt-[16px] pb-[8px] text-center md:text-center'>
            <DrawerTitle className='text-xl-heading text-text-primary-day'>{t('writeMessenger.header')}</DrawerTitle>
            <DrawerDescription className='text-s'>{t('callModal.sub_header')}</DrawerDescription>
          </DrawerHeader>
          <div className='flex flex-col gap-[16px] px-[16px] pt-[8px] pb-[calc(24px+env(safe-area-inset-bottom))]'>
            {content}
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className='sm:max-w-[400px] rounded-[24px]'>
        <DialogHeader className='items-center text-center sm:text-center'>
          <DialogTitle>{t('writeMessenger.header')}</DialogTitle>
          <DialogDescription>{t('callModal.sub_header')}</DialogDescription>
        </DialogHeader>
        {content}
      </DialogContent>
    </Dialog>
  );
};
